import { useEffect, useState } from 'react';

import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@reach/tabs';
import '@reach/tabs/styles.css';
import { ALPHA2_TO_ALPHA3 } from 'modules/constants';
import { useI18n } from 'modules/i18n';
import { RefTreeContainer } from 'modules/interview-references';
import {
    InterviewSearchContainer,
    useInterviewSearch,
} from 'modules/interview-search';
import { useSearchParams } from 'modules/query-string';
import { useProject } from 'modules/routes';
import { TableOfContentsContainer } from 'modules/toc';
import { Transcript } from 'modules/transcript';
import PropTypes from 'prop-types';
import {
    FaList,
    FaRegClone,
    FaRegFileAlt,
    FaSearch,
    FaTags,
} from 'react-icons/fa';

import LocaleSwitch from './LocaleSwitch';
import showTocTab from './showTocTab';
import showTranslationTab from './showTranslationTab';

export default function InterviewTabs({ interview }) {
    const { t, locale } = useI18n();
    const { project } = useProject();
    const [tabIndex, setTabIndex] = useState(0);
    const [translationLocale, setTranslationLocale] = useState(
        interview.translation_alpha3
    );

    const { fulltext } = useSearchParams();
    const { numResults } = useInterviewSearch(
        interview.archive_id,
        fulltext,
        project
    );

    useEffect(() => {
        selectTabOnInit();
    }, []);

    useEffect(() => {
        checkSelectedTabAfterLocaleChange();
    }, [locale]);

    function selectTabOnInit() {
        if (directlyShowSearchResults()) {
            setTabIndex(3);
        } else if (translatedTranscriptIsMoreSuitable()) {
            setTabIndex(1);
        } else {
            setTabIndex(0);
        }
    }

    function directlyShowSearchResults() {
        return fulltext && numResults > 0;
    }

    function translatedTranscriptIsMoreSuitable() {
        return (
            locale !== interview.alpha2 &&
            showTranslationTab(project, interview, locale)
        );
    }

    function checkSelectedTabAfterLocaleChange() {
        // When changing locales sometimes a formerly selected tab is not
        // available any more.
        if (selectedTabNotAvailable()) {
            setTabIndex(0);
        }
    }

    function selectedTabNotAvailable() {
        return (
            (tabIndex === 1 &&
                !showTranslationTab(project, interview, locale)) ||
            (tabIndex === 2 && !showTocTab(project, interview, locale))
        );
    }

    return (
        <Tabs
            className="Tabs"
            keyboardActivation="manual"
            index={tabIndex}
            onChange={setTabIndex}
            data-testid="interview-tabs"
        >
            <div className="Layout-contentTabs">
                <TabList
                    className="Tabs-tabList"
                    data-testid="interview-tabs-list"
                >
                    <Tab
                        className="Tabs-tab"
                        data-testid="interview-tab-transcript"
                    >
                        <FaRegFileAlt className="Tabs-tabIcon" />
                        <span className="Tabs-tabText">
                            {`${t('transcript')} (${interview.alpha3})`}
                        </span>
                    </Tab>
                    <Tab
                        className="Tabs-tab"
                        disabled={
                            !showTranslationTab(project, interview, locale)
                        }
                        data-testid="interview-tab-translation"
                    >
                        <FaRegClone className="Tabs-tabIcon" />
                        <span className="Tabs-tabText">
                            {t('translation')}
                            <LocaleSwitch
                                alpha3s={interview.translation_alpha3s}
                                selected={translationLocale}
                                setTranslationLocale={setTranslationLocale}
                            />
                        </span>
                    </Tab>
                    <Tab
                        className="Tabs-tab"
                        disabled={!showTocTab(project, interview, locale)}
                        data-testid="interview-tab-toc"
                    >
                        <FaList className="Tabs-tabIcon" />
                        <span className="Tabs-tabText">
                            {t('table_of_contents')}
                        </span>
                    </Tab>
                    <Tab
                        className="Tabs-tab"
                        data-testid="interview-tab-search"
                    >
                        <FaSearch className="Tabs-tabIcon" />
                        <span className="Tabs-tabText">
                            {t('interview_search')}
                        </span>
                    </Tab>
                    <Tab
                        className="Tabs-tab"
                        data-testid="interview-tab-keywords"
                    >
                        <FaTags className="Tabs-tabIcon" />
                        <span className="Tabs-tabText">{t('keywords')}</span>
                    </Tab>
                </TabList>
            </div>

            <div className="wrapper-content">
                <TabPanels>
                    {/* The conditional renderings are needed to prevent
                        various useEffect-related problems. */}
                    <TabPanel data-testid="interview-panel-transcript">
                        {tabIndex === 0 && (
                            <Transcript
                                transcriptLocale={interview.alpha3}
                                originalLocale
                                loadSegments
                            />
                        )}
                    </TabPanel>
                    <TabPanel data-testid="interview-panel-translation">
                        {tabIndex === 1 && (
                            <Transcript transcriptLocale={translationLocale} />
                        )}
                    </TabPanel>
                    <TabPanel data-testid="interview-panel-toc">
                        {tabIndex === 2 && (
                            <TableOfContentsContainer
                                alpha3={ALPHA2_TO_ALPHA3[locale]}
                            />
                        )}
                    </TabPanel>
                    <TabPanel data-testid="interview-panel-search">
                        {tabIndex === 3 && <InterviewSearchContainer />}
                    </TabPanel>
                    <TabPanel data-testid="interview-panel-keywords">
                        {tabIndex === 4 && <RefTreeContainer />}
                    </TabPanel>
                </TabPanels>
            </div>
        </Tabs>
    );
}

InterviewTabs.propTypes = {
    interview: PropTypes.object.isRequired,
};
